import React from 'react'
import { Link } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { ArrowLeft, Printer } from 'lucide-react'

// THE INVOICE AS A DOCUMENT (docs/plans/2026-09-09-billing-payers.md §11.3).
//
// An invoice is a legal document, and until this page existed the operator could not open one: the
// Billing table showed a number, a period and a total, and staff had the only readable copy. This
// is the thing an accountant prints, so it is typeset as a sheet — not laid out as a dashboard.
//
// IT COMPOSES NOTHING. Every string on it — the money, the dates, the period, the status sentence,
// the VAT line or the note that replaces it, the payment line — arrives finished from
// `Operator::BillingController#invoice_document_props`. The raw `…Amount` twins exist only for
// alignment and for deciding whether a block is there at all. There is no date math and no money
// math anywhere in this file (R-FE-MATH), and no URL is assembled here: `backHref` is served whole.
//
// Printing is a stylesheet, not infrastructure. `window.print()` plus the `@media print` block in
// entrypoints/operator.css (scoped by the `invoice-print` body class this page sets while mounted)
// drops every piece of chrome — header, billing strip, footer, the back link and this button — and
// prints the sheet alone, black on white.

interface InvoiceLine {
  id: number
  description: string
  detail: string | null
  billedRooms: number | null
  billedRoomsLabel: string | null
  unitPriceLabel: string
  unitPriceAmount: number
  discountLabel: string | null
  discountPct: number
  amountLabel: string
  amountAmount: number
}

interface InvoiceTotals {
  subtotalLabel: string
  subtotalAmount: number
  vatLabel: string
  vatAmount: number
  vatRateLabel: string | null
  vatNote: string | null
  totalLabel: string
  totalAmount: number
}

interface InvoiceDocumentData {
  id: number
  number: string
  kind: string
  kindLabel: string
  status: string
  statusLabel: string
  statusTone: string
  statusSentence: string
  period: string
  issuedOn: string | null
  dueOn: string | null
  paidOn: string | null
  currency: string
  seller: {
    brandName: string
    legalName: string
    supportEmail: string
    vatNumber: string | null
  }
  billTo: {
    accountName: string
    paymentMethodName: string | null
    invoiceEmail: string | null
  }
  lines: InvoiceLine[]
  totals: InvoiceTotals
  payment: { amountLabel: string; amount: number; paidOn: string | null; sentence: string } | null
}

interface InvoiceDocumentProps {
  currentUser: { id: number; email: string }
  /** The Billing page this document belongs to, served whole by the backend. */
  backHref: string
  invoice: InvoiceDocumentData
}

// The chip tone is DECIDED SERVER-SIDE (`statusTone`) so this document and the invoice table can
// never disagree about what a status looks like. This map is the design system's palette for the
// five tones and nothing more.
const TONE_CLASSES: Record<string, string> = {
  paid: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  due: 'border-amber-200 bg-amber-50 text-amber-700',
  overdue: 'border-rose-200 bg-rose-50 text-rose-700',
  failed: 'border-rose-200 bg-rose-50 text-rose-700',
  draft: 'border-border bg-muted text-muted-foreground',
}

export default function InvoiceDocument({ currentUser, backHref, invoice }: InvoiceDocumentProps) {
  // The print scope. Everything the print stylesheet hides is keyed off this class, so no other
  // page's printing is touched, and the class leaves with the page.
  React.useEffect(() => {
    document.body.classList.add('invoice-print')
    return () => document.body.classList.remove('invoice-print')
  }, [])

  const { seller, billTo, totals, payment, lines } = invoice
  // A column that exists only when a line actually carries a discount. A conditional on a
  // backend-composed string, never a calculation.
  const showDiscount = lines.some((line) => !!line.discountLabel)
  const tone = TONE_CLASSES[invoice.statusTone] ?? TONE_CLASSES.draft

  return (
    <PortfolioLayout title={`Invoice ${invoice.number}`} currentUser={currentUser}>
      <div className="mx-auto max-w-3xl space-y-4">
        <div
          className="print-hide flex flex-wrap items-center justify-between gap-3 animate-slide-in"
          style={{ animationDelay: '0ms' }}
        >
          <Link
            href={backHref}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Billing &amp; plan
          </Link>
          <Button onClick={() => window.print()} variant="outline" size="sm" className="h-8 rounded-lg">
            <Printer className="mr-1.5 h-3.5 w-3.5" />
            Print or save as PDF
          </Button>
        </div>

        <article
          className="invoice-sheet rounded-xl border border-border/60 bg-white p-8 shadow-sm animate-slide-in sm:p-10"
          style={{ animationDelay: '50ms' }}
        >
          {/* Seller left, document meta right. */}
          <header className="flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0">
              <p className="text-lg font-semibold lowercase tracking-tight text-foreground">{seller.brandName}</p>
              <p className="mt-1 text-xs text-muted-foreground">{seller.legalName}</p>
              <p className="text-xs text-muted-foreground">{seller.supportEmail}</p>
              {seller.vatNumber && (
                <p className="mt-1 text-xs tabular-nums text-muted-foreground">VAT {seller.vatNumber}</p>
              )}
            </div>
            <div className="min-w-0 sm:text-right">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {invoice.kindLabel}
              </p>
              <p className="mt-1 text-2xl font-bold tracking-tight tabular-nums text-foreground">{invoice.number}</p>
              <div className="mt-2 sm:flex sm:justify-end">
                <Badge variant="outline" className={`text-[11px] ${tone}`}>
                  {invoice.statusLabel}
                </Badge>
              </div>
              <p className="mt-2 max-w-xs text-xs text-muted-foreground sm:ml-auto">{invoice.statusSentence}</p>
            </div>
          </header>

          <div className="mt-8 grid grid-cols-1 gap-6 border-t border-border/40 pt-6 sm:grid-cols-2">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Billed to</p>
              <p className="mt-2 text-sm font-semibold text-foreground">{billTo.accountName}</p>
              {billTo.paymentMethodName && (
                <p className="mt-0.5 text-sm text-muted-foreground">{billTo.paymentMethodName}</p>
              )}
              {billTo.invoiceEmail && (
                <p className="mt-0.5 text-sm text-muted-foreground">{billTo.invoiceEmail}</p>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:justify-items-end">
              <MetaField label="Period" value={invoice.period} />
              {invoice.issuedOn && <MetaField label="Issued" value={invoice.issuedOn} />}
              {invoice.dueOn && <MetaField label="Due" value={invoice.dueOn} />}
              {invoice.paidOn && <MetaField label="Paid" value={invoice.paidOn} />}
            </dl>
          </div>

          <div className="mt-8 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Rooms</TableHead>
                  <TableHead className="text-right">Unit price</TableHead>
                  {showDiscount && <TableHead className="text-right">Discount</TableHead>}
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <span className="font-medium text-foreground">{line.description}</span>
                      {line.detail && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{line.detail}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                      {line.billedRoomsLabel ?? '—'}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                      {line.unitPriceLabel}
                    </TableCell>
                    {showDiscount && (
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {line.discountLabel ?? '—'}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-semibold tabular-nums text-foreground">
                      {line.amountLabel}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-6 flex justify-end">
            <dl className="w-full max-w-xs space-y-2 text-sm">
              <TotalRow label="Subtotal" value={totals.subtotalLabel} />
              {/* A document, unlike a table row, cannot go quiet about a tax: it prints the rate
                  line when the invoice carries VAT, and the backend's stated note when it does not. */}
              {totals.vatRateLabel ? (
                <TotalRow label={totals.vatRateLabel} value={totals.vatLabel} />
              ) : (
                totals.vatNote && <p className="text-xs text-muted-foreground">{totals.vatNote}</p>
              )}
              <div className="flex items-baseline justify-between gap-6 border-t border-border/60 pt-2">
                <dt className="text-sm font-semibold text-foreground">Total</dt>
                <dd className="text-base font-bold tabular-nums text-foreground">{totals.totalLabel}</dd>
              </div>
            </dl>
          </div>

          {payment && (
            <div className="mt-8 rounded-lg border border-border/60 bg-muted/40 px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Payment</p>
              <p className="mt-1 text-sm text-foreground">{payment.sentence}</p>
            </div>
          )}

          <footer className="mt-8 border-t border-border/40 pt-4 text-xs text-muted-foreground">
            Questions about this invoice? Write to {seller.supportEmail}.
          </footer>
        </article>
      </div>
    </PortfolioLayout>
  )
}

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div className="sm:text-right">
      <dt className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm tabular-nums text-foreground">{value}</dd>
    </div>
  )
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums text-foreground">{value}</dd>
    </div>
  )
}
